/**
 * "That person in the tree is me" — docs/05 §11.4.
 *
 * An account and a person are different things, and most people in a family
 * tree will never have an account — they are ancestors, or children, or simply
 * not interested. But every account *is* someone in the tree, and a cousin who
 * joins usually finds themselves already there, entered years ago by an aunt.
 *
 * Before this, connecting the two was an administrator's job, so the member had
 * to ask. Now they say it themselves and an administrator confirms — which keeps
 * the easy half easy without letting anyone quietly become another person.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PersonSummary } from '@famtree/shared';
import { usersApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { PersonPicker } from './PersonPicker.js';
import ui from '../../ui/components.module.css';

export function ClaimIdentity(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<PersonSummary | null>(null);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const claim = useMutation({
    mutationFn: (personId: string) => usersApi.claim(personId),
    onSuccess: refresh,
  });
  const withdraw = useMutation({
    mutationFn: () => usersApi.withdrawClaim(),
    onSuccess: refresh,
  });

  // Nothing to say to someone already linked.
  if (!user || user.person_id) return null;

  if (user.claimed_person_id) {
    return (
      <div className={ui.notice} role="status">
        <p style={{ marginBlockStart: 0 }}>{t('claim.waiting')}</p>
        <button
          type="button"
          className={ui.buttonSecondary}
          disabled={withdraw.isPending}
          onClick={() => withdraw.mutate()}
        >
          {t('claim.withdraw')}
        </button>
        {withdraw.isError && <p className={ui.problem}>{problemMessage(withdraw.error, t)}</p>}
      </div>
    );
  }

  return (
    <section className={ui.card}>
      <h2>{t('claim.title')}</h2>
      <p className={ui.muted}>{t('claim.help')}</p>

      <PersonPicker label={t('claim.whoAreYou')} value={picked} onChange={setPicked} />

      <button
        type="button"
        className={ui.button}
        disabled={!picked || claim.isPending}
        onClick={() => picked && claim.mutate(picked.id)}
      >
        {t('claim.submit')}
      </button>
      {claim.isError && <p className={ui.problem}>{problemMessage(claim.error, t)}</p>}
      <p className={ui.muted}>{t('claim.notThere')}</p>
    </section>
  );
}
